import { Component, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  /** True when the failure was a failed data load rather than a render crash. */
  network: boolean;
}

/**
 * IT support contact for the Sikhio Digital Twin deployment. Wired here
 * so a mayor staring at a blank screen on his phone knows exactly who to call.
 * Thai is canonical; English is the safety net for English-speaking staff.
 */
const SUPPORT_CONTACT = "support@nonarkara.org";

/**
 * A render crash and a dead API look identical to the operator unless we say
 * which it is, and they need different responses: a crash is a deploy to roll
 * back, a dead API is a network/route to chase. Telling the Mayor to "refresh"
 * for a ReferenceError sends on-call at the wrong system entirely.
 */
function isNetworkFault(error: Error): boolean {
  return /\b(fetch failed|networkerror|failed to fetch|econnrefused|etimedout|load failed)\b/i.test(error.message);
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, network: false };

  static getDerivedStateFromError(error: unknown): State {
    const err = error instanceof Error ? error : new Error(String(error));
    return { hasError: true, error: err, network: isNetworkFault(err) };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const network = this.state.network;
      return (
        <div className="gateway-screen" role="alert">
          <div className="gateway-card">
            <a className="gateway-seal" href="https://sikhiotown.go.th" rel="noreferrer">
              <img src="/logos/sikhio-seal.png" alt="เทศบาลเมืองสีคิ้ว" />
            </a>
            <p className="gateway-eyebrow mono">
              STM-01 · {network ? "GATEWAY" : "APPLICATION FAULT"}
            </p>
            <h1 className="gateway-title">
              {network ? (
                <>
                  ระบบเชื่อมต่อขัดข้อง
                  <span>Connection interrupted</span>
                </>
              ) : (
                <>
                  ระบบแสดงผลผิดพลาด
                  <span>Application error</span>
                </>
              )}
            </h1>
            <p className="gateway-body">
              {network ? (
                <>
                  แดชบอร์ดไม่สามารถโหลดข้อมูลได้ — มักเกิดจากเกตเวย์หรือเครือข่าย
                  กรุณารีเฟรช หากยังไม่หาย ติดต่อทีมสนับสนุน
                </>
              ) : (
                <>
                  แดชบอร์ดพบข้อผิดพลาดของโปรแกรม ไม่ใช่ปัญหาเครือข่าย
                  การรีเฟรชไม่ช่วย — กรุณาติดต่อทีมสนับสนุนทันที พร้อมรหัสข้อผิดพลาดด้านล่าง
                </>
              )}
            </p>
            <p className="gateway-body gateway-body-en">
              {network ? (
                <>
                  The twin could not load (often a transient gateway / network fault).
                  Reload the page. If it persists, contact support.
                </>
              ) : (
                <>
                  A code fault stopped the dashboard — not a network problem.
                  Reloading will not help; send the error reference below to support.
                </>
              )}
            </p>
            <div className="gateway-actions">
              <button type="button" className="gateway-btn gateway-btn-primary mono" onClick={() => window.location.reload()}>
                รีเฟรช · RELOAD
              </button>
              <a className="gateway-btn mono" href={`mailto:${SUPPORT_CONTACT}`}>
                {SUPPORT_CONTACT}
              </a>
            </div>
            {this.state.error && (
              <details className="gateway-details" open={!network}>
                <summary className="mono">Technical details</summary>
                <pre>{this.state.error.message}</pre>
              </details>
            )}
            <p className="gateway-foot mono">Axiom · Innovation as a Service</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
